import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { AdminTenant } from './useTenants';
import { useReopenTenant } from './useOffboarding';

/**
 * Reopen a closed account (slice 27) — the recovery from a close.
 *
 * Reopening is the undo of a mistake and must NOT be harder than making one, so
 * this is a single confirm, no typed name. It clears the closure columns; access
 * is restored by the same predicate that blocked it and the full history returns
 * (it was never touched). Released hardware does NOT come back — that is a
 * separate re-provisioning, by design, and the copy says so.
 */
export function ReopenDialog({
  tenant,
  onClose,
}: {
  tenant: AdminTenant;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const [error, setError] = useState(false);
  const reopen = useReopenTenant(tenant.id);

  async function submit() {
    setError(false);
    try {
      await reopen.mutateAsync();
      onClose();
    } catch {
      setError(true);
    }
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={t('offboard.reopen.title', { tenant: tenant.name })}
      className="fixed inset-0 z-50 flex items-stretch justify-center overflow-y-auto bg-black/60 p-0 sm:items-start sm:p-8"
    >
      <div className="w-full min-h-dvh rounded-none border-0 bg-raad-panel p-5 pt-[calc(1.25rem+var(--raad-safe-top))] sm:min-h-0 sm:max-w-md sm:rounded-2xl sm:border sm:border-raad-border sm:p-6 sm:pt-6">
        <div className="mb-4 flex items-start justify-between gap-4">
          <h2 className="text-[17px] font-semibold">
            {t('offboard.reopen.title', { tenant: tenant.name })}
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="min-h-9 rounded-lg border border-raad-border px-3 text-[12.5px] font-semibold"
          >
            {t('offboard.reopen.close')}
          </button>
        </div>

        <p className="rounded-lg border border-raad-border bg-raad-bg/40 p-3 text-[12.5px] text-raad-muted">
          {t('offboard.reopen.body', { tenant: tenant.name })}
        </p>
        <p className="mt-2 rounded-lg border border-raad-border bg-raad-bg/40 p-3 text-[12.5px] text-raad-muted">
          {t('offboard.reopen.hardwareNote')}
        </p>

        {error ? (
          <p role="alert" className="mt-3 text-[12.5px] text-raad-error">
            {t('offboard.reopen.failed')}
          </p>
        ) : null}

        <div className="mt-5 flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            disabled={reopen.isPending}
            className="min-h-10 rounded-lg border border-raad-border px-4 text-[13px] font-semibold disabled:opacity-40"
          >
            {t('offboard.reopen.cancel')}
          </button>
          <button
            type="button"
            onClick={() => void submit()}
            disabled={reopen.isPending}
            className="min-h-10 rounded-lg bg-raad-primary px-4 text-[13px] font-semibold text-raad-onprimary disabled:opacity-40"
          >
            {reopen.isPending ? t('offboard.reopen.reopening') : t('offboard.reopen.confirmButton')}
          </button>
        </div>
      </div>
    </div>
  );
}
